/**
 * Courier Delivery React Hooks
 * Epic 71: Futaros Kezbesites
 *
 * TanStack Query hooks for courier delivery management.
 */

import {
  createPaymentLink,
  getCourierDeliveries,
  getCourierDeliveryById,
  handoffToCourier,
  markDelivered,
  requestCourierDelivery,
  updateCourierStatus,
  type CourierDelivery,
  type CourierHandoffInput,
  type CreatePaymentLinkInput,
  type RequestDeliveryInput,
} from '@/api/courier-delivery';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// ============================================
// QUERIES
// ============================================

export function useCourierDeliveries(worksheetId?: string, status?: string) {
  const query = useQuery({
    queryKey: ['courier-deliveries', worksheetId, status],
    queryFn: async () => {
      const res = await getCourierDeliveries(worksheetId, status);
      return res.data;
    },
  });

  return {
    deliveries: (query.data ?? []) as CourierDelivery[],
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

export function useCourierDelivery(id: string | undefined) {
  const query = useQuery({
    queryKey: ['courier-deliveries', id],
    queryFn: async () => {
      if (!id) throw new Error('No delivery ID');
      const res = await getCourierDeliveryById(id);
      return res.data;
    },
    enabled: !!id,
  });

  return {
    delivery: query.data as CourierDelivery | undefined,
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

// ============================================
// MUTATIONS
// ============================================

export function useRequestDelivery() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: RequestDeliveryInput) => requestCourierDelivery(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courier-deliveries'] });
    },
  });
}

export function useUpdateCourierStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => updateCourierStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courier-deliveries'] });
    },
  });
}

export function useHandoffToCourier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CourierHandoffInput }) =>
      handoffToCourier(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courier-deliveries'] });
    },
  });
}

export function useMarkDelivered() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => markDelivered(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courier-deliveries'] });
    },
  });
}

export function useCreatePaymentLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ deliveryId, data }: { deliveryId: string; data: CreatePaymentLinkInput }) =>
      createPaymentLink(deliveryId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courier-deliveries'] });
    },
  });
}
